import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import EmptyState from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useReports } from '@/hooks/use-reports';
import { useUsers } from '@/hooks/use-users';
import { showSuccessToast } from '@/lib/utils';
import { authService } from '@/services/authService';
import { gmailService } from '@/services/gmailService';
import { addWeeks, endOfWeek, format, isWithinInterval, startOfWeek, subWeeks } from 'date-fns';
import {
    AlertTriangle,
    Bell,
    Calendar,
    CheckCircle2,
    ChevronLeft,
    ChevronRight,
    Loader2,
    Send,
    Users
} from 'lucide-react';
import React, { useMemo, useState } from 'react';

const Reminders: React.FC = () => {
    const { user } = useAuth();
    const { reports, loading: reportsLoading } = useReports();
    const { users, loading: usersLoading } = useUsers();
    const { toast } = useToast();
    const [loadingWeek, setLoadingWeek] = useState<string | null>(null);
    const [weeksToShow, setWeeksToShow] = useState<number>(8);
    const [startDate, setStartDate] = useState<Date | undefined>(undefined);

    const loading = reportsLoading || usersLoading;

    // Generate weeks data
    const weeks = useMemo(() => {
        const result: { startDate: Date; endDate: Date; label: string }[] = [];
        const today = new Date();
        const baseDate = startDate || today;
        let currentDate = subWeeks(baseDate, weeksToShow - 1);
        for (let i = 0; i < weeksToShow; i++) {
            const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 4);
            weekEnd.setHours(23, 59, 59, 999);
            result.push({
                startDate: weekStart,
                endDate: weekEnd,
                label: `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`
            });
            currentDate = addWeeks(currentDate, 1);
        }
        return result.reverse();
    }, [weeksToShow, startDate]);

    // Compliance calculation logic
    const complianceData = useMemo(() => {
        if (!users || !weeks) return [];
        return users.map(user => {
            const userData = {
                user: { id: user.id, name: user.name, email: user.email },
                weeks: {} as Record<string, { hasSentReport: boolean; reports: any[] }>,
                missedWeeks: 0
            };
            const userReports = reports.filter(report => report.createdBy === user.email);
            weeks.forEach(week => {
                const weekKey = format(week.startDate, 'yyyy-MM-dd');
                const reportsThisWeek = userReports.filter(report => {
                    if (report.date) {
                        try {
                            const reportDate = new Date(report.date);
                            if (!isNaN(reportDate.getTime()) && isWithinInterval(reportDate, {
                                start: week.startDate,
                                end: week.endDate
                            })) {
                                return true;
                            }
                        } catch {
                            // ignore errors
                        }
                    }
                    if (report.subject) {
                        const subject = report.subject.toLowerCase().trim();
                        const weekLabel = week.label.toLowerCase().trim();
                        const dateRangeMatch = subject.match(/([a-z]{3,9} \d{1,2}) ?- ?([a-z]{3,9} \d{1,2}, \d{4})/i);
                        if (dateRangeMatch) {
                            const startStr = dateRangeMatch[1] + ', ' + dateRangeMatch[2].split(',')[1];
                            const endStr = dateRangeMatch[2];
                            const startDate = new Date(startStr);
                            const endDate = new Date(endStr);
                            if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
                                if (
                                    isWithinInterval(week.startDate, { start: startDate, end: endDate }) ||
                                    isWithinInterval(week.endDate, { start: startDate, end: endDate }) ||
                                    isWithinInterval(startDate, { start: week.startDate, end: week.endDate })
                                ) {
                                    return true;
                                }
                            }
                        }
                        if (subject.includes(weekLabel)) {
                            return true;
                        }
                    }
                    if (report.createdAt) {
                        try {
                            const createdDate = new Date(report.createdAt);
                            if (!isNaN(createdDate.getTime()) && isWithinInterval(createdDate, {
                                start: week.startDate,
                                end: week.endDate
                            })) {
                                return true;
                            }
                        } catch {
                            // ignore errors
                        }
                    }
                    return false;
                });
                userData.weeks[weekKey] = {
                    hasSentReport: reportsThisWeek.length > 0,
                    reports: reportsThisWeek
                };
            });
            userData.missedWeeks = Object.values(userData.weeks).filter(week => !week.hasSentReport).length;
            return userData;
        });
    }, [users, weeks, reports]);

    // For each week, collect all users missing a report
    const missingByWeek = useMemo(() => {
        if (!weeks) return [];
        return weeks.map(week => {
            const weekKey = format(week.startDate, 'yyyy-MM-dd');
            const missingUsers = complianceData
                .filter(userData => userData.weeks[weekKey] && !userData.weeks[weekKey].hasSentReport)
                .map(userData => userData.user);
            return { week, missingUsers };
        });
    }, [weeks, complianceData]);

    // Calculate summary stats
    const summaryStats = useMemo(() => {
        const totalWeeks = missingByWeek.length;
        const weeksWithMissing = missingByWeek.filter(w => w.missingUsers.length > 0).length;
        const totalMissingCount = missingByWeek.reduce((acc, w) => acc + w.missingUsers.length, 0);
        const complianceRate = totalWeeks > 0 
            ? Math.round(((totalWeeks - weeksWithMissing) / totalWeeks) * 100)
            : 100;
        return { totalWeeks, weeksWithMissing, totalMissingCount, complianceRate };
    }, [missingByWeek]);

    // Bulk send reminders for a week
    const sendReminders = async (week: { startDate: Date; endDate: Date; label: string }, missingUsers: { id: string; name: string; email: string }[]) => {
        if (!user) {
            toast({
                title: 'Authentication error',
                description: 'No user found. Please log in again.',
                variant: 'destructive',
            });
            return;
        }

        setLoadingWeek(week.label);
        try {
            const access_token = await gmailService.getValidAccessToken();

            if (!access_token) {
                toast({
                    title: 'Gmail error',
                    description: 'Could not get Gmail access token. Please re-authenticate with Google.',
                    variant: 'destructive',
                });
                setLoadingWeek(null);
                return;
            }

            const userEmails = missingUsers.map(u => u.email);
            const userNames = missingUsers.map(u => u.name);

            const res = await fetch('/api/admin/send-reminder', {
                method: 'POST',
                headers: authService.getAuthHeaders(),
                body: JSON.stringify({
                    userEmails,
                    userNames,
                    weekLabel: week.label,
                    access_token,
                }),
            });

            if (res.ok) {
                showSuccessToast(`Reminder sent to ${userEmails.length} users for ${week.label}`);
            } else {
                const error = await res.text();
                toast({
                    title: 'Reminder failed',
                    description: error || `Failed to send reminder for ${week.label}`,
                    variant: 'destructive',
                });
            }
        } catch (err: any) {
            toast({
                title: 'Reminder failed',
                description: `Failed to send reminder for ${week.label}: ${err.message}`,
                variant: 'destructive',
            });
        }
        setLoadingWeek(null);
    };

    return (
        <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-card">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Total Users</p>
                                <p className="text-2xl font-bold text-foreground">
                                    {loading ? <Skeleton className="h-8 w-12" /> : users.length}
                                </p>
                            </div>
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                <Users className="h-5 w-5 text-primary" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-card">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Weeks Tracked</p>
                                <p className="text-2xl font-bold text-foreground">{weeksToShow}</p>
                            </div>
                            <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                                <Calendar className="h-5 w-5 text-cyan-500" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-card">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Missing Reports</p>
                                <p className="text-2xl font-bold text-foreground">
                                    {loading ? <Skeleton className="h-8 w-12" /> : summaryStats.totalMissingCount}
                                </p>
                            </div>
                            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                                <AlertTriangle className="h-5 w-5 text-amber-500" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-card">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Compliance Rate</p>
                                <p className="text-2xl font-bold text-foreground">
                                    {loading ? <Skeleton className="h-8 w-12" /> : `${summaryStats.complianceRate}%`}
                                </p>
                            </div>
                            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Week Selector */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="flex items-center gap-2">
                                <Bell className="h-5 w-5 text-primary" />
                                Bulk Reminders
                            </CardTitle>
                            <CardDescription>
                                Send reminder emails to users who haven&apos;t submitted their weekly reports
                            </CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setWeeksToShow(Math.max(4, weeksToShow - 4))}
                                disabled={weeksToShow <= 4}
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <span className="text-sm text-muted-foreground px-2">
                                {weeksToShow} weeks
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setWeeksToShow(Math.min(16, weeksToShow + 4))}
                                disabled={weeksToShow >= 16}
                            >
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="space-y-3">
                            {[1, 2, 3, 4].map(i => (
                                <Skeleton key={i} className="h-20" />
                            ))}
                        </div>
                    ) : missingByWeek.length === 0 ? (
                        <EmptyState
                            icon={Calendar}
                            title="No weeks to display"
                            description="Adjust the week range to see compliance data."
                        />
                    ) : (
                        <div className="space-y-3">
                            {missingByWeek.map(({ week, missingUsers }, index) => (
                                <div
                                    key={week.label}
                                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg border border-border bg-secondary/20 gap-4 animate-slide-up"
                                    style={{ animationDelay: `${index * 30}ms` }}
                                >
                                    {/* Week Info */}
                                    <div className="flex items-center gap-4">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                            missingUsers.length === 0 
                                                ? 'bg-emerald-500/10 text-emerald-500' 
                                                : 'bg-amber-500/10 text-amber-500'
                                        }`}>
                                            {missingUsers.length === 0 
                                                ? <CheckCircle2 className="h-5 w-5" />
                                                : <AlertTriangle className="h-5 w-5" />
                                            }
                                        </div>
                                        <div>
                                            <p className="font-medium text-foreground">{week.label}</p>
                                            <p className="text-sm text-muted-foreground">
                                                {missingUsers.length === 0 
                                                    ? 'All reports submitted'
                                                    : `${missingUsers.length} missing report${missingUsers.length > 1 ? 's' : ''}`
                                                }
                                            </p>
                                        </div>
                                    </div>

                                    {/* Missing Users */}
                                    <div className="flex flex-wrap items-center gap-2 flex-1 sm:justify-center">
                                        {missingUsers.length > 0 ? (
                                            <>
                                                {missingUsers.slice(0, 3).map(u => (
                                                    <Badge key={u.email} variant="missing" className="text-xs">
                                                        {u.name || u.email.split('@')[0]}
                                                    </Badge>
                                                ))}
                                                {missingUsers.length > 3 && (
                                                    <Badge variant="secondary" className="text-xs">
                                                        +{missingUsers.length - 3} more
                                                    </Badge>
                                                )}
                                            </>
                                        ) : (
                                            <Badge variant="sent" className="text-xs">
                                                Complete
                                            </Badge>
                                        )}
                                    </div>

                                    {/* Action Button */}
                                    <Button
                                        variant={missingUsers.length === 0 ? "outline" : "default"}
                                        size="sm"
                                        disabled={missingUsers.length === 0 || loadingWeek === week.label}
                                        onClick={() => sendReminders(week, missingUsers)}
                                        className="gap-2 whitespace-nowrap"
                                    >
                                        {loadingWeek === week.label ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <Send className="h-4 w-4" />
                                                Send Reminders
                                            </>
                                        )}
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default Reminders;
